import React, { useState, useRef, useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { FiDollarSign, FiFileText, FiCheckCircle, FiMinusCircle } from "react-icons/fi";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import StatusChip from "../../../Common/StatusChip";
import DateRangeFilter from "../../../../Common/DateFilter";
import ReportCard from "../../../Common/ReportCard";
import { RA_BILL_REGISTER_DATA } from "./worksReportsData";

const SubcontractorRABillRegisterReport = () => {
  const gridRef = useRef(null);
  const [data] = useState(RA_BILL_REGISTER_DATA);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [contractorFilter, setContractorFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [columnVisibility, setColumnVisibility] = useState({});

  // Summary KPI metrics
  const kpis = useMemo(() => {
    const totalGross = data.reduce((s, r) => s + (r.grossCertified || 0), 0);
    const totalNetPayable = data.reduce((s, r) => s + (r.netPayable || 0), 0);
    const totalRetention = data.reduce((s, r) => s + (r.retentionWithheld || 0), 0);
    const totalDeductions = data.reduce(
      (s, r) =>
        s + (r.mobilizationRecovery || 0) + (r.whtDeducted || 0) + (r.materialDeductions || 0),
      0,
    );

    return {
      totalGross,
      totalNetPayable,
      totalRetention,
      totalDeductions,
    };
  }, [data]);

  const uniqueContractors = useMemo(() => {
    return Array.from(new Set(data.map((d) => d.contractorName).filter(Boolean)));
  }, [data]);

  const getBillChipType = (status) => {
    if (status === "Paid / Settled") {
      return "success";
    }
    if (status === "Approved") {
      return "info";
    }
    if (status === "Under Review") {
      return "warning";
    }
    return "default";
  };

  // Column definitions
  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Bill / RA No.",
        field: "billNo",
        colId: "billNo",
        flex: 1.2,
        minWidth: 140,
        cellRenderer: (p) => (
          <span
            style={{
              fontWeight: 700,
              fontFamily: "monospace",
              color: "var(--color-primary, #0b6462)",
            }}
          >
            {p.value}
          </span>
        ),
      },
      {
        headerName: "Date & Linked WO",
        field: "submissionDate",
        colId: "submissionDate",
        flex: 1.5,
        minWidth: 160,
        cellRenderer: (p) => (
          <div>
            <div style={{ fontWeight: 600, color: "#1e293b" }}>{p.value}</div>
            <small style={{ color: "#64748b", fontFamily: "monospace" }}>
              {p.data?.workOrderRef}
            </small>
          </div>
        ),
      },
      {
        headerName: "Contractor Name",
        field: "contractorName",
        colId: "contractorName",
        flex: 1.8,
        minWidth: 190,
        cellRenderer: (p) => <span style={{ fontWeight: 600, color: "#0f172a" }}>{p.value}</span>,
      },
      {
        headerName: "Gross Work Certified (PKR)",
        field: "grossCertified",
        colId: "grossCertified",
        flex: 1.5,
        minWidth: 160,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 700, color: "#0f172a" }}>
            PKR {p.value ? Number(p.value).toLocaleString() : "0"}
          </span>
        ),
      },
      {
        headerName: "Retention (5%)",
        field: "retentionWithheld",
        colId: "retentionWithheld",
        flex: 1.2,
        minWidth: 130,
        cellRenderer: (p) => (
          <span style={{ color: "#d97706", fontWeight: 600 }}>
            -PKR {p.value ? Number(p.value).toLocaleString() : "0"}
          </span>
        ),
      },
      {
        headerName: "Mob. Recovery",
        field: "mobilizationRecovery",
        colId: "mobilizationRecovery",
        flex: 1.2,
        minWidth: 130,
        cellRenderer: (p) => (
          <span style={{ color: "#475569" }}>
            {p.value ? `-PKR ${Number(p.value).toLocaleString()}` : "—"}
          </span>
        ),
      },
      {
        headerName: "WHT Deducted",
        field: "whtDeducted",
        colId: "whtDeducted",
        flex: 1.2,
        minWidth: 130,
        cellRenderer: (p) => (
          <span style={{ color: "#64748b" }}>
            -PKR {p.value ? Number(p.value).toLocaleString() : "0"}
          </span>
        ),
      },
      {
        headerName: "Contra / Material Charges",
        field: "materialDeductions",
        colId: "materialDeductions",
        flex: 1.3,
        minWidth: 140,
        cellRenderer: (p) => (
          <span style={{ color: p.value ? "#dc2626" : "#94a3b8" }}>
            {p.value ? `-PKR ${Number(p.value).toLocaleString()}` : "—"}
          </span>
        ),
      },
      {
        headerName: "Net Payable (PKR)",
        field: "netPayable",
        colId: "netPayable",
        flex: 1.5,
        minWidth: 160,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 700, color: "#0b6462" }}>
            PKR {p.value ? Number(p.value).toLocaleString() : "0"}
          </span>
        ),
      },
      {
        headerName: "Bill Status",
        field: "status",
        colId: "status",
        flex: 1.3,
        minWidth: 140,
        cellRenderer: (p) => <StatusChip status={p.value} type={getBillChipType(p.value)} />,
      },
    ];
  }, []);

  // Filter logic
  const filteredData = useMemo(() => {
    return data.filter((row) => {
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const matches =
          (row.billNo && row.billNo.toLowerCase().includes(q)) ||
          (row.workOrderRef && row.workOrderRef.toLowerCase().includes(q)) ||
          (row.contractorName && row.contractorName.toLowerCase().includes(q));
        if (!matches) {
          return false;
        }
      }

      if (statusFilter !== "all" && row.status !== statusFilter) {
        return false;
      }

      if (contractorFilter !== "all" && row.contractorName !== contractorFilter) {
        return false;
      }

      if (dateRange.from && row.submissionDate && row.submissionDate < dateRange.from) {
        return false;
      }
      if (dateRange.to && row.submissionDate && row.submissionDate > dateRange.to) {
        return false;
      }

      return true;
    });
  }, [data, searchTerm, statusFilter, contractorFilter, dateRange]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: false,
      resizable: true,
    }),
    [],
  );

  const toggleColumn = useCallback((colId) => {
    setColumnVisibility((prev) => {
      const next = { ...prev, [colId]: !prev[colId] };
      if (gridRef.current?.api) {
        gridRef.current.api.setColumnsVisible([colId], next[colId]);
      }
      return next;
    });
  }, []);

  const handleExportCSV = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.exportDataAsCsv({
        fileName: `Subcontractor_RA_Bill_Register_${new Date().toISOString().slice(0, 10)}.csv`,
      });
      toast.success("RA bill register exported to CSV");
    }
  };

  return (
    <div className="pt-2 app_font">
      {/* ── Summary Cards ── */}
      <div className="row g-2 mb-1 mt-1">
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="TOTAL GROSS CERTIFIED"
            value={`PKR ${(kpis.totalGross / 1000000).toFixed(2)}M`}
            subtitle="Gross value of submitted RA bills"
            icon={FiFileText}
            color="#0b6462"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="NET PAYABLE CERTIFIED"
            value={`PKR ${(kpis.totalNetPayable / 1000000).toFixed(2)}M`}
            subtitle="Payable after statutory and advance deductions"
            icon={FiDollarSign}
            color="#16a34a"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="RETENTION WITHHELD"
            value={`PKR ${(kpis.totalRetention / 1000).toFixed(0)}K`}
            subtitle="Security guarantee withheld at 5%"
            icon={FiMinusCircle}
            color="#d97706"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="TOTAL DEDUCTIONS"
            value={`PKR ${(kpis.totalDeductions / 1000000).toFixed(2)}M`}
            subtitle="Mob advance, WHT, and contra debits"
            icon={FiCheckCircle}
            color="#0284c7"
          />
        </div>
      </div>

      {/* ── Filter Toolbar (Search First, then Date, then other filters) ── */}
      <div className="row app_font">
        <div className="col-12 pt-1 p-0">
          <div
            className="d-flex flex-wrap align-items-center justify-content-end pt-2 pb-2 app_font w-100"
            style={{ gap: "12px", width: "100%" }}
          >
            <div
              className="d-flex align-items-center"
              style={{
                minHeight: "30px",
                maxWidth: "100%",
                marginRight: "auto",
                padding: "4px 12px",
                color: "var(--btn-comp-text, #0b6462)",
                fontSize: "var(--font-md)",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
              title="Contractor RA Bills Report"
            >
              Contractor RA Bills Report
            </div>
            {/* Search Filter FIRST */}
            <SearchFilter
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search bill no, contractor, WO..."
            />

            {/* Date Filter SECOND */}
            <div className="d-flex align-items-center" style={{ maxWidth: "260px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <DateRangeFilter
                  initialFrom={dateRange.from}
                  initialTo={dateRange.to}
                  onChange={(val) => setDateRange(val)}
                />
                <label>Date</label>
              </div>
            </div>

            {/* Status Filter THIRD */}
            <div className="d-flex align-items-center" style={{ maxWidth: "200px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Bill Status</option>
                  <option value="Draft">Draft</option>
                  <option value="Under Review">Under Review</option>
                  <option value="Approved">Approved</option>
                  <option value="Paid / Settled">Paid / Settled</option>
                </select>
                <label>Bill Status</label>
              </div>
            </div>

            {/* Contractor Filter */}
            <div className="d-flex align-items-center" style={{ maxWidth: "220px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={contractorFilter}
                  onChange={(e) => setContractorFilter(e.target.value)}
                >
                  <option value="all">All Contractors</option>
                  {uniqueContractors.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <label>Contractor</label>
              </div>
            </div>

            <ColumnVisibilityControl
              gridApi={gridRef.current?.api}
              columnVisibility={columnVisibility}
              toggleColumn={toggleColumn}
            />
          </div>
        </div>
      </div>

      {/* ── AG Grid Table ── */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div
            className="ag-theme-alpine vouchers_table_height3 mt-1"
            style={{ width: "100%", position: "relative" }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={15}
              animateRows={true}
            />
            {/* Table Footer Actions */}
            <div
              style={{
                position: "absolute",
                bottom: "6px",
                left: "15px",
                display: "flex",
                gap: "10px",
                zIndex: 1,
              }}
            >
              <ActionButton
                text="Download CSV"
                isPrint={true}
                newBtn="true"
                onClick={handleExportCSV}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubcontractorRABillRegisterReport;
